import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Shield, IndianRupee, CheckCircle2, XCircle, Image as ImageIcon, TrendingUp, TrendingDown, Wallet } from "lucide-react";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminDamageClaims() {
  const [tab, setTab] = useState("pending");
  const [pool, setPool] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [approveAmount, setApproveAmount] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/damage/pool"),
        api.get(`/damage/claims?status=${tab}`),
      ]);
      setPool(p.data);
      setClaims(c.data.claims || []);
    } catch (err) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [tab]);

  const viewDetail = async (id) => {
    try {
      const { data } = await api.get(`/damage/claims/${id}`);
      setDetail(data);
      setApproveAmount(String(data.amount_requested));
    } catch (err) { toast.error("Failed to load"); }
  };

  const approve = async () => {
    setActionLoading(true);
    try {
      await api.post(`/damage/claims/${detail._id}/approve`, { amount_approved: parseFloat(approveAmount) });
      toast.success("Claim approved");
      setDetail(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Approve failed"); }
    finally { setActionLoading(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason required"); return; }
    setActionLoading(true);
    try {
      await api.post(`/damage/claims/${rejectFor}/reject`, { reason: rejectReason });
      toast.success("Claim rejected");
      setRejectFor(null); setRejectReason(""); setDetail(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Reject failed"); }
    finally { setActionLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-damage-claims">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Damage Claims &amp; Provision Pool</h1>
        <p className="text-sm text-slate-500 mt-0.5">1.5% of every delivered order is auto-credited to the pool. Claims are paid from the pool.</p>
      </div>

      {/* Pool cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <PoolCard icon={Wallet} label="Current Pool Balance" value={pool?.balance} highlight />
        <PoolCard icon={TrendingUp} label="Total Credited" value={pool?.total_credited} />
        <PoolCard icon={TrendingDown} label="Total Paid Out" value={pool?.total_paid_out} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${tab === t.key ? "bg-[#FF6B00] text-white" : "bg-white border border-slate-200 text-slate-600"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-100">
        {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
          : claims.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No claims in this tab</div>
          : <div className="divide-y divide-slate-100">
              {claims.map((c) => (
                <div key={c._id} className="p-5 hover:bg-slate-50/40" data-testid={`claim-row-${c._id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-sm font-bold text-[#FF6B00]">{c.tracking_id}</span>
                        <span className="text-xs font-semibold text-slate-900">{c.user_name}</span>
                        <span className="text-[9px] font-bold uppercase bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{c.reason}</span>
                        <span className="text-[9px] font-bold uppercase bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{c.photo_count} photo(s)</span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{c.description}</p>
                      <div className="flex gap-3 mt-2 text-xs">
                        <span>Order ₹{c.order_total}</span>
                        <span className="font-bold text-[#FF6B00]">Requested ₹{c.amount_requested}</span>
                        {c.amount_approved && <span className="font-bold text-emerald-600">Approved ₹{c.amount_approved}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => viewDetail(c._id)} className="rounded-lg" data-testid={`view-claim-${c._id}`}>Review</Button>
                      {c.status === "pending" && <Button size="sm" variant="outline" onClick={() => setRejectFor(c._id)} className="rounded-lg border-red-200 text-red-600" data-testid={`reject-claim-${c._id}`}>Reject</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between">
              <div>
                <h3 className="font-['Outfit'] text-lg font-bold">{detail.tracking_id}</h3>
                <p className="text-xs text-slate-500">{detail.user_name} · {detail.reason}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>✕</Button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid sm:grid-cols-3 gap-3">
                <Info label="Order Total" value={`₹${detail.order_total}`} />
                <Info label="Requested" value={`₹${detail.amount_requested}`} />
                <Info label="Status" value={detail.status} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Description</p>
                <p className="text-sm text-slate-700">{detail.description}</p>
              </div>
              {detail.photos_base64?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Photos</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {detail.photos_base64.map((p, i) => (
                      <a key={`${detail.id || detail._id || 'claim'}-photo-${i}`} href={p} target="_blank" rel="noreferrer" className="block">
                        <img src={p} alt={`Evidence ${i + 1}`} className="h-28 w-full rounded-lg object-cover border border-slate-100" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {detail.rejection_reason && <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                <p className="text-xs font-bold text-red-900">Rejected: <span className="font-semibold">{detail.rejection_reason}</span></p>
              </div>}
              {detail.status === "pending" && (
                <div className="border-t border-slate-100 pt-5">
                  <Label>Amount to Approve (₹)</Label>
                  <Input type="number" value={approveAmount} onChange={(e) => setApproveAmount(e.target.value)} className="mt-1" data-testid="approve-amount-input" />
                  <div className="flex gap-2 mt-4">
                    <Button onClick={approve} disabled={actionLoading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl h-11" data-testid="approve-btn">
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve &amp; Credit Wallet</>}
                    </Button>
                    <Button variant="outline" onClick={() => setRejectFor(detail._id)} className="flex-1 border-red-200 text-red-600 rounded-xl h-11">
                      <XCircle className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => !actionLoading && setRejectFor(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="reject-dialog">
            <h3 className="font-['Outfit'] text-lg font-bold mb-1">Reject Claim</h3>
            <Label>Reason</Label>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="w-full mt-1 rounded-xl border border-slate-200 p-3 text-sm h-24 resize-none" data-testid="reject-reason" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setRejectFor(null)} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={reject} disabled={actionLoading} className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl" data-testid="confirm-reject">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PoolCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "bg-gradient-to-br from-orange-50 to-white border-orange-200" : "bg-white border-slate-100"}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className={`font-['Outfit'] text-3xl font-bold ${highlight ? "text-[#FF6B00]" : "text-slate-900"}`}>₹{(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value || "—"}</p>
    </div>
  );
}
